/**
 * Dialog de configuração para node Criar Negócio CRM
 * Permite configurar hierarquia Workspace→Pipeline→Fase, origem, vendedor, título e produtos
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo dialog de configuração CRM
 * RELEASE: 1.1.0 - 2025-01-15 - Padronização do layout e uso de VariablePickerCombobox
 * RELEASE: 2.0.0 - 2025-01-15 - Verificação de duplicidade com escopo, janela de recorrência e comportamento
 * RELEASE: 5.0.0 - 2026-03-10 - Adicionado seção de Produtos/Serviços pré-configurados
 * RELEASE: 5.1.0 - 2026-09-07 - Destinos de saída migrados para o modal de variáveis graváveis.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InputWithVariables } from '../InputWithVariables';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel as SelectGroupLabel,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
// ScrollArea removido - usando scroll nativo para robustez (v2.1.0)
import { 
  Briefcase, 
  MapPin, 
  Tag, 
  User, 
  FileText, 
  Variable, 
  HelpCircle,
  Shield,
  Clock,
  X,
  CheckCircle,
  AlertTriangle,
  Package,
  Plus,
} from 'lucide-react';
import { FlowNode, CrmCreateDealConfig, CrmDuplicateBehavior, RecurrenceWindowUnit } from '@/types/flows';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { LEAD_SOURCES, getLeadSourceIcon } from '@/lib/lead-sources';
import { showToast } from '@/lib/toast';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { ProductCatalogModal, DealItemsSummary } from '@/components/crm/deals/modal/tabs/products';
import { useCrmProducts } from '@/hooks/useCrmProducts';

interface ConfigureCrmCreateDealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: CrmCreateDealConfig) => void;
}

/**
 * Interface para produto pendente no estado local do dialog
 * Usado para pré-configurar produtos antes de salvar o node
 */
interface PendingProduct {
  id: string; // UUID local para identificação no DealItemsSummary
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  total: number;
  product_type?: 'product' | 'service';
  sku?: string | null;
}

/**
 * Interface para item de seleção (workspace/pipeline/fase)
 */
interface ScopeItem {
  id: string;
  name: string;
  color?: string;
  parentName?: string; // Para exibir contexto (ex: "Pipeline: Vendas")
}

/**
 * Dialog para configurar criação de negócio CRM
 * Hierarquia em cascata: Workspace → Pipeline → Fase
 * RELEASE 2.0.0: Inclui verificação de duplicidade
 */
export function ConfigureCrmCreateDealDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureCrmCreateDealDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || '';
  
  // Estado do formulário
  const existingConfig = node.data.config as CrmCreateDealConfig | undefined;
  
  // Estado para dialog de ajuda
  const [showHelp, setShowHelp] = useState(false);
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // ============================================
  // CAMPOS DE CRIAÇÃO (existentes)
  // ============================================
  
  // Hierarquia: Workspace → Pipeline → Fase
  const [workspaceId, setWorkspaceId] = useState(existingConfig?.workspace_id || '');
  const [workspaceName, setWorkspaceName] = useState(existingConfig?.workspace_name || '');
  const [pipelineId, setPipelineId] = useState(existingConfig?.pipeline_id || '');
  const [pipelineName, setPipelineName] = useState(existingConfig?.pipeline_name || '');
  const [phaseId, setPhaseId] = useState(existingConfig?.phase_id || '');
  const [phaseName, setPhaseName] = useState(existingConfig?.phase_name || '');
  const [phaseColor, setPhaseColor] = useState(existingConfig?.phase_color || '');
  
  // Informações do negócio
  const [source, setSource] = useState(existingConfig?.source || 'chatbot');
  const [sourceLabel, setSourceLabel] = useState(existingConfig?.source_label || '');
  
  // ============================================
  // MODO DE ATRIBUIÇÃO DE VENDEDOR (v3.0.0)
  // ============================================
  
  // Determina modo inicial baseado na config existente
  const [sellerAssignmentMode, setSellerAssignmentMode] = useState<'manual' | 'distribution_queue' | 'none'>(
    existingConfig?.seller_assignment_mode || 
    (existingConfig?.seller_id ? 'manual' : 'none')
  );
  
  // Vendedor manual
  const [sellerId, setSellerId] = useState(existingConfig?.seller_id || '');
  const [sellerName, setSellerName] = useState(existingConfig?.seller_name || '');
  
  // Fila de distribuição
  const [distributionQueueId, setDistributionQueueId] = useState(existingConfig?.distribution_queue_id || '');
  const [distributionQueueName, setDistributionQueueName] = useState(existingConfig?.distribution_queue_name || '');
  const [distributionQueueMethod, setDistributionQueueMethod] = useState(existingConfig?.distribution_queue_method || '');
  
  // Atribuir conversa ao vendedor do deal (v4.0.0)
  const [assignConversationToSeller, setAssignConversationToSeller] = useState(
    existingConfig?.assign_conversation_to_seller || false
  );
  
  // Título do deal
  const [titleMode, setTitleMode] = useState<'static' | 'from_variable' | 'auto'>(
    existingConfig?.title_mode || 'auto'
  );
  const [titleStatic, setTitleStatic] = useState(existingConfig?.title_static || '');
  
  // Variável do título (leitura)
  const [titleVariableId, setTitleVariableId] = useState(existingConfig?.title_variable_id || '');
  const [titleVariableName, setTitleVariableName] = useState(existingConfig?.title_variable || '');
  
  // Variável de saída (escrita)
  const [outputVariableId, setOutputVariableId] = useState(existingConfig?.output_variable_id || '');
  const [outputVariableName, setOutputVariableName] = useState(existingConfig?.output_variable || '');

  // ============================================
  // CAMPOS DE VERIFICAÇÃO DE DUPLICIDADE (v2.0.0)
  // ============================================
  
  const [duplicateCheckEnabled, setDuplicateCheckEnabled] = useState(
    existingConfig?.duplicate_check_enabled || false
  );
  
  // Escopo de verificação (multi-select)
  const [checkWorkspaceIds, setCheckWorkspaceIds] = useState<string[]>(
    existingConfig?.check_workspace_ids || []
  );
  const [checkWorkspaceNames, setCheckWorkspaceNames] = useState<string[]>(
    existingConfig?.check_workspace_names || []
  );
  const [checkPipelineIds, setCheckPipelineIds] = useState<string[]>(
    existingConfig?.check_pipeline_ids || []
  );
  const [checkPipelineNames, setCheckPipelineNames] = useState<string[]>(
    existingConfig?.check_pipeline_names || []
  );
  const [checkPhaseIds, setCheckPhaseIds] = useState<string[]>(
    existingConfig?.check_phase_ids || []
  );
  const [checkPhaseNames, setCheckPhaseNames] = useState<string[]>(
    existingConfig?.check_phase_names || []
  );
  
  // Janela de recorrência
  const [recurrenceWindowValue, setRecurrenceWindowValue] = useState<number>(
    existingConfig?.recurrence_window_value || 30
  );
  const [recurrenceWindowUnit, setRecurrenceWindowUnit] = useState<RecurrenceWindowUnit>(
    existingConfig?.recurrence_window_unit || 'days'
  );
  
  // Comportamento quando encontrar duplicata
  const [duplicateBehavior, setDuplicateBehavior] = useState<CrmDuplicateBehavior>(
    existingConfig?.duplicate_behavior || 'skip_if_open'
  );
  
  // Variável de saída do resultado de duplicidade (v2.1.0)
  const [duplicateResultVariableId, setDuplicateResultVariableId] = useState(
    existingConfig?.duplicate_result_variable_id || ''
  );
  const [duplicateResultVariableName, setDuplicateResultVariableName] = useState(
    existingConfig?.duplicate_result_variable || ''
  );

  // ============================================
  // PRODUTOS/SERVIÇOS PRÉ-CONFIGURADOS (v5.0.0)
  // ============================================
  
  /** Produtos selecionados em estado local (memória) */
  const [pendingProducts, setPendingProducts] = useState<PendingProduct[]>(() => {
    // Inicializa com produtos da config existente
    return (existingConfig?.products || []).map((p, idx) => ({
      id: `local-${idx}-${p.product_id}`,
      product_id: p.product_id,
      product_name: p.product_name,
      quantity: p.quantity || 1,
      unit_price: p.unit_price || 0,
      discount_percent: p.discount_percent || 0,
      total: ((p.quantity || 1) * (p.unit_price || 0)) * (1 - (p.discount_percent || 0) / 100),
      product_type: p.product_type,
      sku: p.sku,
    }));
  });
  
  /** Controla abertura do modal de catálogo de produtos */
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);

  // Garante que ao abrir outro node, os dados corretos sejam carregados
  // ============================================
  useEffect(() => {
    if (!open) return; // So sincroniza quando o modal esta aberto
    
    const cfg = node.data.config as CrmCreateDealConfig | undefined;
    
    // Hierarquia
    setWorkspaceId(cfg?.workspace_id || '');
    setWorkspaceName(cfg?.workspace_name || '');
    setPipelineId(cfg?.pipeline_id || '');
    setPipelineName(cfg?.pipeline_name || '');
    setPhaseId(cfg?.phase_id || '');
    setPhaseName(cfg?.phase_name || '');
    setPhaseColor(cfg?.phase_color || '');
    
    // Informacoes do negocio
    setSource(cfg?.source || 'chatbot');
    setSourceLabel(cfg?.source_label || '');
    
    // Vendedor
    setSellerAssignmentMode(
      cfg?.seller_assignment_mode || (cfg?.seller_id ? 'manual' : 'none')
    );
    setSellerId(cfg?.seller_id || '');
    setSellerName(cfg?.seller_name || '');
    setDistributionQueueId(cfg?.distribution_queue_id || '');
    setDistributionQueueName(cfg?.distribution_queue_name || '');
    setDistributionQueueMethod(cfg?.distribution_queue_method || '');
    setAssignConversationToSeller(cfg?.assign_conversation_to_seller || false);
    
    // Titulo
    setTitleMode(cfg?.title_mode || 'auto');
    setTitleStatic(cfg?.title_static || '');
    setTitleVariableId(cfg?.title_variable_id || '');
    setTitleVariableName(cfg?.title_variable || '');
    setOutputVariableId(cfg?.output_variable_id || '');
    setOutputVariableName(cfg?.output_variable || '');
    
    // Duplicidade
    setDuplicateCheckEnabled(cfg?.duplicate_check_enabled || false);
    setCheckWorkspaceIds(cfg?.check_workspace_ids || []);
    setCheckWorkspaceNames(cfg?.check_workspace_names || []);
    setCheckPipelineIds(cfg?.check_pipeline_ids || []);
    setCheckPipelineNames(cfg?.check_pipeline_names || []);
    setCheckPhaseIds(cfg?.check_phase_ids || []);
    setCheckPhaseNames(cfg?.check_phase_names || []);
    setRecurrenceWindowValue(cfg?.recurrence_window_value || 30);
    setRecurrenceWindowUnit(cfg?.recurrence_window_unit || 'days');
    setDuplicateBehavior(cfg?.duplicate_behavior || 'skip_if_open');
    setDuplicateResultVariableId(cfg?.duplicate_result_variable_id || '');
    setDuplicateResultVariableName(cfg?.duplicate_result_variable || '');
    
    // Produtos pré-configurados (v5.0.0)
    setPendingProducts((cfg?.products || []).map((p, idx) => ({
      id: `local-${idx}-${p.product_id}`,
      product_id: p.product_id,
      product_name: p.product_name,
      quantity: p.quantity || 1,
      unit_price: p.unit_price || 0,
      discount_percent: p.discount_percent || 0,
      total: ((p.quantity || 1) * (p.unit_price || 0)) * (1 - (p.discount_percent || 0) / 100),
      product_type: p.product_type,
      sku: p.sku,
    })));
    setCatalogModalOpen(false);
  }, [node.id, open]);

  // ============================================
  // QUERIES
  // ============================================

  // Query: Workspaces ativos
  const { data: workspaces, isLoading: loadingWorkspaces } = useQuery({
    queryKey: ['crm-workspaces-active', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_workspaces')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  // Query: Pipelines do workspace selecionado (para criação)
  const { data: pipelines, isLoading: loadingPipelines } = useQuery({
    queryKey: ['crm-pipelines-by-workspace', tenantId, workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_pipelines')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('workspace_id', workspaceId)
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && !!workspaceId && open,
  });

  // Query: Fases do pipeline selecionado (para criação)
  // CORREÇÃO: Exclui fases de tipo "Ganho" e "Perdido" que requerem campos manuais
  const { data: phases, isLoading: loadingPhases } = useQuery({
    queryKey: ['crm-pipeline-phases-for-creation', pipelineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, color, probability_percent, is_won, is_lost')
        .eq('pipeline_id', pipelineId)
        .eq('is_won', false)   // Exclui fases de "Ganho"
        .eq('is_lost', false)  // Exclui fases de "Perdido"
        .order('sort_order');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!pipelineId && open,
  });

  // Query: Filas de distribuição com contexto CRM (v3.0.0)
  const { data: distributionQueues = [], isLoading: loadingQueues } = useQuery({
    queryKey: ['crm-distribution-queues', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from('distribution_queues')
        .select('id, name, distribution_method, is_active')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .in('context_type', ['crm', 'all'])
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  // Query: Todos os pipelines do tenant (para escopo de verificação)
  const { data: allPipelines } = useQuery({
    queryKey: ['crm-all-pipelines', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_pipelines')
        .select(`
          id, 
          name, 
          workspace_id,
          crm_workspaces!inner(name)
        `)
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open && duplicateCheckEnabled,
  });

  // Query: Todas as fases do tenant (para escopo de verificação)
  const { data: allPhases } = useQuery({
    queryKey: ['crm-all-phases', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select(`
          id, 
          name, 
          color,
          pipeline_id,
          is_won,
          is_lost,
          crm_pipelines!inner(name, workspace_id, crm_workspaces!inner(name))
        `)
        .eq('crm_pipelines.tenant_id', tenantId)
        .order('sort_order');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open && duplicateCheckEnabled,
  });

  // ============================================
  // HANDLERS DE CRIAÇÃO
  // ============================================

  // Efeito: Limpar pipeline e fase quando workspace muda
  useEffect(() => {
    if (workspaceId && workspaceId !== existingConfig?.workspace_id) {
      setPipelineId('');
      setPipelineName('');
      setPhaseId('');
      setPhaseName('');
      setPhaseColor('');
    }
  }, [workspaceId, existingConfig?.workspace_id]);

  // Efeito: Limpar fase quando pipeline muda
  useEffect(() => {
    if (pipelineId && pipelineId !== existingConfig?.pipeline_id) {
      setPhaseId('');
      setPhaseName('');
      setPhaseColor('');
    }
  }, [pipelineId, existingConfig?.pipeline_id]);

  // Handler: Seleção de workspace
  const handleWorkspaceSelect = (id: string) => {
    const workspace = workspaces?.find(w => w.id === id);
    setWorkspaceId(id);
    setWorkspaceName(workspace?.name || '');
  };

  // Handler: Seleção de pipeline
  const handlePipelineSelect = (id: string) => {
    const pipeline = pipelines?.find(p => p.id === id);
    setPipelineId(id);
    setPipelineName(pipeline?.name || '');
  };

  // Handler: Seleção de fase
  const handlePhaseSelect = (id: string) => {
    const phase = phases?.find(p => p.id === id);
    setPhaseId(id);
    setPhaseName(phase?.name || '');
    setPhaseColor(phase?.color || '');
  };

  // Handler: Seleção de origem
  const handleSourceSelect = (id: string) => {
    const leadSource = LEAD_SOURCES.find(s => s.id === id);
    setSource(id);
    setSourceLabel(leadSource?.label || id);
  };

  // Handler: Seleção de vendedor
  const handleSellerSelect = (id: string | null, name?: string) => {
    setSellerId(id || '');
    setSellerName(name || '');
  };

  // ============================================
  // HANDLERS DE ESCOPO DE VERIFICAÇÃO
  // ============================================

  // Handler: Adicionar/Remover workspace do escopo
  const toggleWorkspaceScope = (id: string, name: string) => {
    if (checkWorkspaceIds.includes(id)) {
      setCheckWorkspaceIds(prev => prev.filter(wId => wId !== id));
      setCheckWorkspaceNames(prev => prev.filter((_, i) => checkWorkspaceIds[i] !== id));
    } else {
      setCheckWorkspaceIds(prev => [...prev, id]);
      setCheckWorkspaceNames(prev => [...prev, name]);
    }
  };

  // Handler: Adicionar/Remover pipeline do escopo
  const togglePipelineScope = (id: string, name: string) => {
    if (checkPipelineIds.includes(id)) {
      setCheckPipelineIds(prev => prev.filter(pId => pId !== id));
      setCheckPipelineNames(prev => prev.filter((_, i) => checkPipelineIds[i] !== id));
    } else {
      setCheckPipelineIds(prev => [...prev, id]);
      setCheckPipelineNames(prev => [...prev, name]);
    }
  };

  // Handler: Adicionar/Remover fase do escopo
  const togglePhaseScope = (id: string, name: string) => {
    if (checkPhaseIds.includes(id)) {
      setCheckPhaseIds(prev => prev.filter(phId => phId !== id));
      setCheckPhaseNames(prev => prev.filter((_, i) => checkPhaseIds[i] !== id));
    } else {
      setCheckPhaseIds(prev => [...prev, id]);
      setCheckPhaseNames(prev => [...prev, name]);
    }
  };

  // Handler: Limpar escopo
  const clearScope = (type: 'workspace' | 'pipeline' | 'phase') => {
    switch (type) {
      case 'workspace':
        setCheckWorkspaceIds([]);
        setCheckWorkspaceNames([]);
        break;
      case 'pipeline':
        setCheckPipelineIds([]);
        setCheckPipelineNames([]);
        break;
      case 'phase':
        setCheckPhaseIds([]);
        setCheckPhaseNames([]);
        break;
    }
  };

  // ============================================
  // HANDLERS DE PRODUTOS/SERVIÇOS (v5.0.0)
  // ============================================

  /** Handler para adicionar produto ao estado local via catálogo */
  const handleLocalAddItem = useCallback(async (productId: string, quantity: number, unitPrice: number) => {
    // Verifica se já existe no array
    const exists = pendingProducts.find(p => p.product_id === productId);
    if (exists) return;

    // Busca dados do produto para exibição
    const { data: productData } = await supabase
      .from('crm_products')
      .select('id, name, sku, product_type')
      .eq('id', productId)
      .single();

    const newItem: PendingProduct = {
      id: `local-${Date.now()}-${productId}`,
      product_id: productId,
      product_name: productData?.name || 'Produto',
      quantity,
      unit_price: unitPrice,
      discount_percent: 0,
      total: quantity * unitPrice,
      product_type: productData?.product_type as 'product' | 'service' || 'product',
      sku: productData?.sku || null,
    };

    setPendingProducts(prev => [...prev, newItem]);
  }, [pendingProducts]);

  /** Handler para atualizar quantidade do produto pelo product_id (usado pelo modal) */
  const handleLocalUpdateQuantityByProduct = useCallback(async (productId: string, quantity: number) => {
    setPendingProducts(prev => prev.map(p => {
      if (p.product_id === productId) {
        const subtotal = quantity * p.unit_price;
        const discountAmount = p.discount_percent ? subtotal * (p.discount_percent / 100) : 0;
        return { ...p, quantity, total: subtotal - discountAmount };
      }
      return p;
    }));
  }, []);

  /** Handler para remover produto pelo product_id (usado pelo modal) */
  const handleLocalRemoveByProduct = useCallback(async (productId: string) => {
    setPendingProducts(prev => prev.filter(p => p.product_id !== productId));
  }, []);

  /** Handler para atualizar quantidade pelo id local (usado pelo DealItemsSummary) */
  const handleLocalUpdateQuantity = useCallback((itemId: string, quantity: number) => {
    setPendingProducts(prev => prev.map(p => {
      if (p.id === itemId) {
        const subtotal = quantity * p.unit_price;
        const discountAmount = p.discount_percent ? subtotal * (p.discount_percent / 100) : 0;
        return { ...p, quantity, total: subtotal - discountAmount };
      }
      return p;
    }));
  }, []);

  /** Handler para atualizar desconto pelo id local (usado pelo DealItemsSummary) */
  const handleLocalUpdateDiscount = useCallback((itemId: string, percent: number, fixed: number) => {
    setPendingProducts(prev => prev.map(p => {
      if (p.id === itemId) {
        const subtotal = p.quantity * p.unit_price;
        const percentDiscount = percent ? subtotal * (percent / 100) : 0;
        const fixedDiscount = fixed || 0;
        return { ...p, discount_percent: percent, total: subtotal - percentDiscount - fixedDiscount };
      }
      return p;
    }));
  }, []);

  /** Handler para remover produto pelo id local (usado pelo DealItemsSummary) */
  const handleLocalRemoveItem = useCallback((itemId: string) => {
    setPendingProducts(prev => prev.filter(p => p.id !== itemId));
  }, []);

  /** Mapeamento de itens para o formato esperado pelo ProductCatalogModal */
  const dealItemsForModal = useMemo(() => {
    return pendingProducts.map(p => ({
      product_id: p.product_id,
      quantity: p.quantity,
    }));
  }, [pendingProducts]);

  /** Mapeamento de itens para o formato esperado pelo DealItemsSummary */
  const dealItemsForSummary = useMemo(() => {
    return pendingProducts.map(p => ({
      id: p.id,
      product_id: p.product_id,
      quantity: p.quantity,
      unit_price: p.unit_price,
      discount_percent: p.discount_percent,
      discount_fixed: 0,
      total: p.total,
      product: {
        id: p.product_id,
        name: p.product_name,
        sku: p.sku || null,
        product_type: p.product_type || 'product' as const,
        unit_type: 'un',
      },
    }));
  }, [pendingProducts]);
  // ============================================

  // Validação do formulário
  const isFormValid = () => {
    if (!workspaceId || !pipelineId || !phaseId || !source) return false;
    if (titleMode === 'static' && !titleStatic) return false;
    if (titleMode === 'from_variable' && !titleVariableName) return false;
    return true;
  };

  // Handler: Salvar configuração
  const handleSave = () => {
    if (!isFormValid()) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const config: CrmCreateDealConfig = {
      // Campos de criação
      workspace_id: workspaceId,
      workspace_name: workspaceName,
      pipeline_id: pipelineId,
      pipeline_name: pipelineName,
      phase_id: phaseId,
      phase_name: phaseName,
      phase_color: phaseColor || undefined,
      source,
      source_label: sourceLabel || undefined,
      
      // Modo de atribuição de vendedor (v3.0.0)
      seller_assignment_mode: sellerAssignmentMode,
      
      // Vendedor manual (apenas quando modo = manual)
      seller_id: sellerAssignmentMode === 'manual' ? sellerId : undefined,
      seller_name: sellerAssignmentMode === 'manual' ? sellerName : undefined,
      
      // Fila de distribuição (apenas quando modo = distribution_queue)
      distribution_queue_id: sellerAssignmentMode === 'distribution_queue' ? distributionQueueId : undefined,
      distribution_queue_name: sellerAssignmentMode === 'distribution_queue' ? distributionQueueName : undefined,
      distribution_queue_method: sellerAssignmentMode === 'distribution_queue' ? distributionQueueMethod : undefined,
      title_mode: titleMode,
      title_static: titleMode === 'static' ? titleStatic : undefined,
      title_variable: titleMode === 'from_variable' ? titleVariableName : undefined,
      title_variable_id: titleMode === 'from_variable' ? titleVariableId : undefined,
      output_variable: outputVariableName || undefined,
      output_variable_id: outputVariableId || undefined,
      
      // Campos de verificação de duplicidade
      duplicate_check_enabled: duplicateCheckEnabled,
      check_workspace_ids: duplicateCheckEnabled && checkWorkspaceIds.length > 0 ? checkWorkspaceIds : undefined,
      check_workspace_names: duplicateCheckEnabled && checkWorkspaceNames.length > 0 ? checkWorkspaceNames : undefined,
      check_pipeline_ids: duplicateCheckEnabled && checkPipelineIds.length > 0 ? checkPipelineIds : undefined,
      check_pipeline_names: duplicateCheckEnabled && checkPipelineNames.length > 0 ? checkPipelineNames : undefined,
      check_phase_ids: duplicateCheckEnabled && checkPhaseIds.length > 0 ? checkPhaseIds : undefined,
      check_phase_names: duplicateCheckEnabled && checkPhaseNames.length > 0 ? checkPhaseNames : undefined,
      recurrence_window_value: duplicateCheckEnabled ? recurrenceWindowValue : undefined,
      recurrence_window_unit: duplicateCheckEnabled ? recurrenceWindowUnit : undefined,
      duplicate_behavior: duplicateCheckEnabled ? duplicateBehavior : undefined,
      // Variável de resultado de duplicidade (v2.1.0)
      duplicate_result_variable: duplicateCheckEnabled && duplicateResultVariableName ? duplicateResultVariableName : undefined,
      duplicate_result_variable_id: duplicateCheckEnabled && duplicateResultVariableId ? duplicateResultVariableId : undefined,
      
      // Atribuir conversa ao vendedor do deal (v4.0.0)
      assign_conversation_to_seller: sellerAssignmentMode !== 'none' ? assignConversationToSeller : undefined,
      
      // Produtos/Serviços pré-configurados (v5.0.0)
      products: pendingProducts.length > 0 ? pendingProducts.map(p => ({
        product_id: p.product_id,
        product_name: p.product_name,
        quantity: p.quantity,
        unit_price: p.unit_price,
        discount_percent: p.discount_percent || undefined,
        product_type: p.product_type,
        sku: p.sku,
      })) : undefined,
    };

    onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
    onOpenChange(false);
  };

  // ============================================
  // MEMOIZED DATA
  // ============================================

  // Lista de pipelines filtrados pelos workspaces selecionados no escopo
  const filteredPipelinesForScope = useMemo(() => {
    if (!allPipelines) return [];
    if (checkWorkspaceIds.length === 0) return allPipelines;
    return allPipelines.filter(p => checkWorkspaceIds.includes(p.workspace_id));
  }, [allPipelines, checkWorkspaceIds]);

  // Lista de fases filtradas pelos pipelines selecionados no escopo
  const filteredPhasesForScope = useMemo(() => {
    if (!allPhases) return [];
    if (checkPipelineIds.length === 0) {
      // Se não tem pipeline selecionado, filtra por workspace
      if (checkWorkspaceIds.length === 0) return allPhases;
      return allPhases.filter(ph => {
        const pipeline = allPipelines?.find(p => p.id === ph.pipeline_id);
        return pipeline && checkWorkspaceIds.includes(pipeline.workspace_id);
      });
    }
    return allPhases.filter(ph => checkPipelineIds.includes(ph.pipeline_id));
  }, [allPhases, allPipelines, checkPipelineIds, checkWorkspaceIds]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* v2.1.0: Padrão robusto de scroll - altura fixa + scroll nativo */}
        <DialogContent className="max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-violet-500" />
                Configurar Criar Negócio
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
            <DialogDescription>
              Configure a criação automática de negócio no CRM
            </DialogDescription>
          </DialogHeader>

          {/* Body com scroll nativo - compatível com wheel, trackpad e drag */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              {/* Campo ID do Node */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* Seção: Destino do Negócio */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <MapPin className="h-4 w-4" />
                  Destino do Negócio
                </div>

                {/* Workspace */}
                <div className="space-y-2">
                  <Label htmlFor="workspace">Workspace *</Label>
                  <Select value={workspaceId} onValueChange={handleWorkspaceSelect}>
                    <SelectTrigger id="workspace" disabled={loadingWorkspaces}>
                      <SelectValue placeholder={loadingWorkspaces ? 'Carregando...' : 'Selecione o workspace'} />
                    </SelectTrigger>
                    <SelectContent>
                      {workspaces?.map((workspace) => (
                        <SelectItem key={workspace.id} value={workspace.id}>
                          <div className="flex items-center gap-2">
                            {workspace.color && (
                              <span 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: workspace.color }}
                              />
                            )}
                            {workspace.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pipeline */}
                <div className="space-y-2">
                  <Label htmlFor="pipeline">Pipeline *</Label>
                  <Select 
                    value={pipelineId} 
                    onValueChange={handlePipelineSelect}
                    disabled={!workspaceId || loadingPipelines}
                  >
                    <SelectTrigger id="pipeline">
                      <SelectValue placeholder={
                        !workspaceId 
                          ? 'Selecione o workspace primeiro' 
                          : loadingPipelines 
                            ? 'Carregando...' 
                            : 'Selecione o pipeline'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {pipelines?.map((pipeline) => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Fase */}
                <div className="space-y-2">
                  <Label htmlFor="phase">Fase Inicial *</Label>
                  <Select 
                    value={phaseId} 
                    onValueChange={handlePhaseSelect}
                    disabled={!pipelineId || loadingPhases}
                  >
                    <SelectTrigger id="phase">
                      <SelectValue placeholder={
                        !pipelineId 
                          ? 'Selecione o pipeline primeiro' 
                          : loadingPhases 
                            ? 'Carregando...' 
                            : 'Selecione a fase inicial'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {phases?.map((phase) => (
                        <SelectItem key={phase.id} value={phase.id}>
                          <div className="flex items-center gap-2">
                            {phase.color && (
                              <span 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: phase.color }}
                              />
                            )}
                            <span>{phase.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* Seção: Informações do Negócio */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <Tag className="h-4 w-4" />
                  Informações do Negócio
                </div>

                {/* Origem */}
                <div className="space-y-2">
                  <Label htmlFor="source">Origem *</Label>
                  <Select value={source} onValueChange={handleSourceSelect}>
                    <SelectTrigger id="source">
                      <SelectValue placeholder="Selecione a origem" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCES.map((leadSource) => {
                        const Icon = getLeadSourceIcon(leadSource.id);
                        return (
                          <SelectItem key={leadSource.id} value={leadSource.id}>
                            <div className="flex items-center gap-2">
                              {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                              {leadSource.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Modo de Atribuição de Vendedor (v3.0.0) */}
                <div className="space-y-3">
                  <Label>Atribuição de Vendedor</Label>
                  <Select 
                    value={sellerAssignmentMode} 
                    onValueChange={(v) => {
                      const mode = v as typeof sellerAssignmentMode;
                      setSellerAssignmentMode(mode);
                      // Limpa campos do modo anterior
                      if (mode !== 'manual') {
                        setSellerId('');
                        setSellerName('');
                      }
                      if (mode !== 'distribution_queue') {
                        setDistributionQueueId('');
                        setDistributionQueueName('');
                        setDistributionQueueMethod('');
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <div className="flex items-center gap-2">
                          <span>Nenhum</span>
                          <span className="text-xs text-muted-foreground">(criar sem vendedor)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="manual">Vendedor Específico</SelectItem>
                      <SelectItem value="distribution_queue">
                        <div className="flex items-center gap-2">
                          <span>Fila de Distribuição</span>
                          <span className="text-xs text-muted-foreground">(automático)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Seletor de Vendedor - aparece quando modo = manual */}
                  {sellerAssignmentMode === 'manual' && (
                    <div className="space-y-2 pt-2 border-l-2 border-violet-500/30 pl-3">
                      <Label>Vendedor</Label>
                      <SellerSelector
                        value={sellerId}
                        onSelect={(id) => handleSellerSelect(id)}
                        placeholder="Selecione um vendedor"
                        allowClear
                      />
                    </div>
                  )}

                  {/* Seletor de Fila - aparece quando modo = distribution_queue */}
                  {sellerAssignmentMode === 'distribution_queue' && (
                    <div className="space-y-2 pt-2 border-l-2 border-emerald-500/30 pl-3">
                      <Label>Fila de Distribuição</Label>
                      <Select
                        value={distributionQueueId}
                        onValueChange={(id) => {
                          const queue = distributionQueues.find(q => q.id === id);
                          setDistributionQueueId(id);
                          setDistributionQueueName(queue?.name || '');
                          setDistributionQueueMethod(queue?.distribution_method || '');
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma fila..." />
                        </SelectTrigger>
                        <SelectContent>
                          {loadingQueues ? (
                            <SelectItem value="_loading" disabled>Carregando filas...</SelectItem>
                          ) : distributionQueues.length === 0 ? (
                            <SelectItem value="_empty" disabled>Nenhuma fila CRM cadastrada</SelectItem>
                          ) : (
                            distributionQueues.map((queue) => (
                              <SelectItem key={queue.id} value={queue.id}>
                                <div className="flex items-center gap-2">
                                  <span>{queue.name}</span>
                                  <Badge variant="secondary" className="text-[10px] px-1 py-0">
                                    {queue.distribution_method === 'round_robin' && 'Round Robin'}
                                    {queue.distribution_method === 'least_busy' && 'Menos Ocupado'}
                                    {queue.distribution_method === 'weighted' && 'Ponderado'}
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        O sistema atribuirá automaticamente um vendedor da fila selecionada
                      </p>
                    </div>
                  )}

                  {/* Toggle: Atribuir conversa ao vendedor (v4.0.0) */}
                  {sellerAssignmentMode !== 'none' && (
                    <div className="space-y-2 pt-2 border-l-2 border-blue-500/30 pl-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="assign-conversation" className="text-xs cursor-pointer">
                          Atribuir conversa ao mesmo vendedor
                        </Label>
                        <Switch
                          id="assign-conversation"
                          checked={assignConversationToSeller}
                          onCheckedChange={setAssignConversationToSeller}
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        A conversa será atribuída automaticamente ao vendedor selecionado ou distribuído para o negócio
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Seção: Título do Negócio */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <FileText className="h-4 w-4" />
                  Título do Negócio
                </div>

                {/* Modo do título */}
                <div className="space-y-2">
                  <Label htmlFor="title-mode">Modo do Título</Label>
                  <Select value={titleMode} onValueChange={(v) => setTitleMode(v as typeof titleMode)}>
                    <SelectTrigger id="title-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">
                        <div className="flex items-center gap-2">
                          <span>Automático</span>
                          <span className="text-xs text-muted-foreground">(Nome do Contato - DD/MM/YYYY)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="static">Texto Fixo</SelectItem>
                      <SelectItem value="from_variable">Ler de Variável</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Título estático com botão de variáveis */}
                {titleMode === 'static' && (
                  <div className="space-y-2">
                    <Label htmlFor="title-static">Título *</Label>
                    <InputWithVariables
                      tenantId={tenantId}
                      value={titleStatic}
                      onChange={setTitleStatic}
                      placeholder="Ex: Novo Lead - {{contact_name}}"
                    />
                    <p className="text-xs text-muted-foreground">
                      Clique no botão {'{}'} para inserir variáveis
                    </p>
                  </div>
                )}

                {/* Variável do título - usando VariablePickerCombobox */}
                {titleMode === 'from_variable' && (
                  <div className="space-y-2">
                    <Label>Variável de Origem *</Label>
                    <VariablePickerCombobox
                      tenantId={tenantId}
                      value={titleVariableId || titleVariableName}
                      onChange={(variable) => {
                        if (variable) {
                          setTitleVariableId(variable.id);
                          setTitleVariableName(variable.name);
                        } else {
                          setTitleVariableId('');
                          setTitleVariableName('');
                        }
                      }}
                      allowCreate={false}
                      allowClear={true}
                      placeholder="Selecione a variável que contém o título"
                    />
                    <p className="text-xs text-muted-foreground">
                      O título do negócio será lido desta variável
                    </p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Seção: Variável de Saída */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <Variable className="h-4 w-4" />
                  Variável de Saída (opcional)
                </div>

                <div className="space-y-2">
                  <Label>Salvar ID do negócio em</Label>
                  <VariableDestinationPicker
                    tenantId={tenantId}
                    value={outputVariableId || outputVariableName}
                    valueName={outputVariableName}
                    onChange={(variable) => {
                      if (variable) {
                        setOutputVariableId(variable.id);
                        setOutputVariableName(variable.name);
                      } else {
                        setOutputVariableId('');
                        setOutputVariableName('');
                      }
                    }}
                    allowClear
                    placeholder="Selecione ou crie uma variável"
                  />
                  <p className="text-xs text-muted-foreground">
                    Útil para usar em nodes posteriores (ex: alterar fase, adicionar nota)
                  </p>
                </div>
              </div>

              <Separator />

              {/* ============================================ */}
              {/* SEÇÃO: VERIFICAÇÃO DE DUPLICIDADE (v2.0.0) */}
              {/* ============================================ */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                    <Shield className="h-4 w-4" />
                    Verificação de Duplicidade
                  </div>
                  <Switch
                    checked={duplicateCheckEnabled}
                    onCheckedChange={setDuplicateCheckEnabled}
                  />
                </div>

                {duplicateCheckEnabled && (
                  <div className="space-y-4 p-3 bg-muted/50 rounded-lg border border-border/50">
                    
                    {/* Escopo de Verificação */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5" />
                        Escopo de Verificação
                      </Label>
                      <p className="text-xs text-muted-foreground -mt-1">
                        Defina onde verificar se já existe negócio para o contato
                      </p>

                      {/* Workspaces */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Workspaces</Label>
                          {checkWorkspaceIds.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2 text-xs"
                              onClick={() => clearScope('workspace')}
                            >
                              Limpar
                            </Button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {workspaces?.map((ws) => (
                            <Badge
                              key={ws.id}
                              variant={checkWorkspaceIds.includes(ws.id) ? 'default' : 'outline'}
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => toggleWorkspaceScope(ws.id, ws.name)}
                            >
                              {ws.color && (
                                <span 
                                  className="w-2 h-2 rounded-full mr-1"
                                  style={{ backgroundColor: ws.color }}
                                />
                              )}
                              {ws.name}
                              {checkWorkspaceIds.includes(ws.id) && (
                                <X className="h-3 w-3 ml-1" />
                              )}
                            </Badge>
                          ))}
                        </div>
                        {checkWorkspaceIds.length === 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Todos os workspaces serão verificados
                          </p>
                        )}
                      </div>

                      {/* Pipelines */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Pipelines</Label>
                          {checkPipelineIds.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2 text-xs"
                              onClick={() => clearScope('pipeline')}
                            >
                              Limpar
                            </Button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                          {filteredPipelinesForScope?.map((pl: any) => (
                            <Badge
                              key={pl.id}
                              variant={checkPipelineIds.includes(pl.id) ? 'default' : 'outline'}
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => togglePipelineScope(pl.id, pl.name)}
                            >
                              {pl.name}
                              <span className="text-[10px] text-muted-foreground ml-1">
                                ({pl.crm_workspaces?.name})
                              </span>
                              {checkPipelineIds.includes(pl.id) && (
                                <X className="h-3 w-3 ml-1" />
                              )}
                            </Badge>
                          ))}
                        </div>
                        {checkPipelineIds.length === 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Todos os pipelines serão verificados
                          </p>
                        )}
                      </div>

                      {/* Fases */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Fases (opcional)</Label>
                          {checkPhaseIds.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2 text-xs"
                              onClick={() => clearScope('phase')}
                            >
                              Limpar
                            </Button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                          {filteredPhasesForScope?.slice(0, 20).map((ph: any) => (
                            <Badge
                              key={ph.id}
                              variant={checkPhaseIds.includes(ph.id) ? 'default' : 'outline'}
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => togglePhaseScope(ph.id, ph.name)}
                            >
                              {ph.color && (
                                <span 
                                  className="w-2 h-2 rounded-full mr-1"
                                  style={{ backgroundColor: ph.color }}
                                />
                              )}
                              {ph.name}
                              {checkPhaseIds.includes(ph.id) && (
                                <X className="h-3 w-3 ml-1" />
                              )}
                            </Badge>
                          ))}
                          {filteredPhasesForScope && filteredPhasesForScope.length > 20 && (
                            <Badge variant="secondary" className="text-xs">
                              +{filteredPhasesForScope.length - 20} fases
                            </Badge>
                          )}
                        </div>
                        {checkPhaseIds.length === 0 && (
                          <p className="text-xs text-muted-foreground">
                            Todas as fases serão verificadas
                          </p>
                        )}
                      </div>
                    </div>

                    <Separator className="my-3" />

                    {/* Janela de Recorrência */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5" />
                        Janela de Recorrência
                      </Label>
                      <p className="text-xs text-muted-foreground -mt-1">
                        Se o último negócio do contato foi criado há menos de X tempo, será considerado existente
                      </p>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          max={365}
                          value={recurrenceWindowValue}
                          onChange={(e) => setRecurrenceWindowValue(Math.max(0, parseInt(e.target.value) || 0))}
                          className="w-20"
                        />
                        <Select 
                          value={recurrenceWindowUnit} 
                          onValueChange={(v) => setRecurrenceWindowUnit(v as RecurrenceWindowUnit)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days">Dias</SelectItem>
                            <SelectItem value="weeks">Semanas</SelectItem>
                            <SelectItem value="months">Meses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-muted-foreground italic">
                        Negócios criados há mais de {recurrenceWindowValue} {
                          recurrenceWindowUnit === 'days' ? 'dias' : 
                          recurrenceWindowUnit === 'weeks' ? 'semanas' : 'meses'
                        } serão ignorados na verificação
                      </p>
                    </div>

                    <Separator className="my-3" />

                    {/* Comportamento se Encontrar */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <CheckCircle className="h-3.5 w-3.5" />
                        Se encontrar negócio existente
                      </Label>
                      <Select 
                        value={duplicateBehavior} 
                        onValueChange={(v) => setDuplicateBehavior(v as CrmDuplicateBehavior)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="always_create">
                            <div className="flex flex-col">
                              <span>Sempre criar novo</span>
                              <span className="text-xs text-muted-foreground">Ignora verificação</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="skip_if_open">
                            <div className="flex flex-col">
                              <span>Ignorar se existir aberto</span>
                              <span className="text-xs text-muted-foreground">Pula criação, retorna ID do existente</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="update_phase">
                            <div className="flex flex-col">
                              <span>Atualizar fase se existir</span>
                              <span className="text-xs text-muted-foreground">Move negócio existente para a fase configurada</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="create_if_all_closed">
                            <div className="flex flex-col">
                              <span>Criar apenas se todos fechados</span>
                              <span className="text-xs text-muted-foreground">Só cria se não houver nenhum aberto no escopo</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator className="my-3" />

                    {/* Variável de saída do resultado de duplicidade (v2.1.0) */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Variable className="h-3.5 w-3.5" />
                        Salvar resultado da verificação em
                      </Label>
                      <p className="text-xs text-muted-foreground -mt-1">
                        Armazena o resultado para usar em nodes de condição
                      </p>
                      <VariableDestinationPicker
                        tenantId={tenantId}
                        value={duplicateResultVariableId || duplicateResultVariableName}
                        valueName={duplicateResultVariableName}
                        onChange={(variable) => {
                          if (variable) {
                            setDuplicateResultVariableId(variable.id);
                            setDuplicateResultVariableName(variable.name);
                          } else {
                            setDuplicateResultVariableId('');
                            setDuplicateResultVariableName('');
                          }
                        }}
                        allowClear
                        placeholder="Selecione ou crie uma variável"
                      />
                      <div className="bg-muted/50 rounded p-2 space-y-1">
                        <p className="text-xs text-muted-foreground font-medium">Valores possíveis:</p>
                        <div className="flex flex-wrap gap-2">
                          <code className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded text-[10px]">
                            created
                          </code>
                          <span className="text-xs text-muted-foreground">Novo negócio criado</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <code className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded text-[10px]">
                            skipped
                          </code>
                          <span className="text-xs text-muted-foreground">Criação ignorada (existente)</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <code className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-[10px]">
                            phase_updated
                          </code>
                          <span className="text-xs text-muted-foreground">Fase atualizada</span>
                        </div>
                      </div>
                    </div>

                  </div>
                )}
              </div>

              <Separator />

              {/* ============================================ */}
              {/* SEÇÃO: PRODUTOS/SERVIÇOS (v5.0.0) */}
              {/* ============================================ */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <Package className="h-4 w-4" />
                  Produtos / Serviços (opcional)
                </div>
                <p className="text-xs text-muted-foreground -mt-2">
                  Pré-configure produtos para serem adicionados automaticamente ao criar o negócio
                </p>

                {/* Botão para abrir catálogo */}
                <Button
                  variant="outline"
                  className="w-full h-10 border-dashed border-2 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                  onClick={() => setCatalogModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Produto ou Serviço
                </Button>

                {/* Resumo dos itens selecionados */}
                {pendingProducts.length > 0 && (
                  <DealItemsSummary
                    items={dealItemsForSummary}
                    currency="BRL"
                    readOnly={false}
                    onUpdateQuantity={handleLocalUpdateQuantity}
                    onUpdateDiscount={handleLocalUpdateDiscount}
                    onRemove={handleLocalRemoveItem}
                    onOpenCatalog={() => setCatalogModalOpen(true)}
                  />
                )}

                {/* Modal de Catálogo de Produtos */}
                <ProductCatalogModal
                  open={catalogModalOpen}
                  onOpenChange={setCatalogModalOpen}
                  dealId="pending"
                  currency="BRL"
                  workspaceId={workspaceId || null}
                  dealItems={dealItemsForModal}
                  onAddItem={handleLocalAddItem}
                  onUpdateItemQuantity={handleLocalUpdateQuantityByProduct}
                  onRemoveItem={handleLocalRemoveByProduct}
                />
              </div>

            </div>
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!isFormValid()}
              className="bg-violet-600 hover:bg-violet-700"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de ajuda */}
      <NodeHelpDialog 
        open={showHelp} 
        onOpenChange={setShowHelp} 
        nodeType="crm_create_deal" 
      />
    </>
  );
}
